import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtDateShort } from "@/lib/api";
import { StatusPill, STATUS_OPTIONS } from "@/lib/orderStatus";
import { StoreFilter, getActiveStore } from "@/components/StoreFilter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function relativeTime(iso) {
  if (!iso) return "never";
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [store, setStore] = useState(getActiveStore());
  const [q, setQ] = useState("");
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const loadOrders = useCallback(() => {
    const params = {};
    if (status !== "all") params.status = status;
    if (store !== "all") params.store = store;
    if (q) params.q = q;
    return api.get("/orders", { params }).then(({ data }) => setOrders(data));
  }, [status, store, q]);

  const loadSyncStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/orders/sync/status");
      setSyncStatus(data);
    } catch (e) {
      console.debug("sync/status fetch failed", e);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    loadSyncStatus();
    const id = setInterval(loadSyncStatus, 30000);
    return () => clearInterval(id);
  }, [loadSyncStatus]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.get("/orders/sync/base44");
      const inserted = data?.inserted ?? 0;
      const updated = data?.updated ?? 0;
      toast.success(
        inserted || updated
          ? `Synced · ${inserted} new, ${updated} updated`
          : "Synced · no changes",
      );
      await Promise.all([loadOrders(), loadSyncStatus()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const totals = useMemo(() => {
    return orders.reduce(
      (acc, o) => {
        const rev = o.total ?? o.items.reduce((s, it) => s + (it.line_total ?? it.unit_price * it.qty), 0);
        const cost = o.items.reduce((s, it) => s + it.unit_cost * it.qty, 0);
        acc.revenue += rev;
        acc.cost += cost;
        acc.profit += rev - cost;
        return acc;
      },
      { revenue: 0, cost: 0, profit: 0 },
    );
  }, [orders]);

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] animate-fade-in">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Orders</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            All orders
          </h1>
          <p className="text-muted-foreground mt-1">
            Pulled from Base44. Filter, search, and drill into any order.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div
            data-testid="orders-sync-status"
            className="text-xs text-muted-foreground text-right leading-tight"
          >
            <div>
              Last synced{" "}
              <span className="font-medium text-foreground">
                {relativeTime(syncStatus?.last_synced_at)}
              </span>
            </div>
            {syncStatus?.auto_sync_running && syncStatus?.next_sync_in_seconds != null && (
              <div>
                Next auto-sync in {Math.max(0, Math.round(syncStatus.next_sync_in_seconds / 60))}m
              </div>
            )}
          </div>
          <Button
            data-testid="orders-sync-now-btn"
            onClick={syncNow}
            disabled={syncing || !syncStatus?.configured}
            size="sm"
            variant="outline"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="label-eyebrow">Filtered revenue</div>
          <div className="mt-2 font-heading text-2xl font-bold num">{fmtCurrency(totals.revenue)}</div>
        </div>
        <div className="stat-card">
          <div className="label-eyebrow">Filtered cost</div>
          <div className="mt-2 font-heading text-2xl font-bold num">{fmtCurrency(totals.cost)}</div>
        </div>
        <div className="stat-card">
          <div className="label-eyebrow">Filtered profit</div>
          <div className="mt-2 font-heading text-2xl font-bold num text-primary">{fmtCurrency(totals.profit)}</div>
        </div>
      </div>

      <div className="surface-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="orders-search"
              placeholder="Search order number or customer"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <StoreFilter value={store} onChange={setStore} testid="orders-store-filter" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="orders-status-filter" className="w-full sm:w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`orders-status-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto -mx-4 sm:-mx-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border/70">
                <th className="text-left font-medium px-4 sm:px-6 py-3">Order</th>
                <th className="text-left font-medium px-4 py-3">Customer</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Date</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Status</th>
                <th className="text-right font-medium px-4 py-3">Items</th>
                <th className="text-right font-medium px-4 sm:px-6 py-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const total = o.total ?? o.items.reduce((s, it) => s + (it.line_total ?? it.unit_price * it.qty), 0);
                return (
                  <tr
                    key={o.id}
                    className="border-b border-border/40 hover:bg-secondary/50 transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3 font-mono">
                      <Link
                        to={`/orders/${o.id}`}
                        data-testid={`order-row-${o.number}`}
                        className="text-primary hover:underline font-medium"
                      >
                        {o.number}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{o.customer.name}</div>
                      <div className="text-xs text-muted-foreground md:hidden">
                        {fmtDateShort(o.created_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{fmtDateShort(o.created_at)}</td>
                    <td className="px-4 py-3 hidden md:table-cell"><StatusPill status={o.status} /></td>
                    <td className="px-4 py-3 text-right num">{o.items.length}</td>
                    <td className="px-4 sm:px-6 py-3 text-right num font-semibold">{fmtCurrency(total)}</td>
                  </tr>
                );
              })}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted-foreground py-10">
                    No orders match the filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
